import type { CityConfig } from "../city-template/types";

export const newcastleDeConfig: CityConfig = {
  city: "New Castle",
  state: "Delaware",
  stateAbbr: "DE",
  slug: "newcastle-de",
  route: "/cities/newcastle-de",
  title: "Emergency Passport New Castle DE | Expedited Passport Historic Riverfront Delaware | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in New Castle, DE — as fast as 24 hours. Fast passport renewal for historic riverfront Delaware residents near the Delaware Memorial Bridge and I-95 corridor. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in New Castle, DE",
  heroSubtitle: "Living near the Delaware River and need a passport fast? Fast Passport Center helps New Castle, Delaware residents near the Delaware Memorial Bridge, Wilmington Airport, and I-95 corridor get their expedited passports as quickly as 24 hours. A+ BBB rated.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Historic%20New%20Castle%20Delaware%20riverfront%20along%20the%20Delaware%20River%2C%20colonial-era%20brick%20buildings%20and%20cobblestone%20streets%2C%20green%20lawns%20and%20historic%20district%2C%20American%20flag%20on%20the%20historic%20courthouse%2C%20peaceful%20waterfront%20with%20small%20boats%20on%20calm%20river%2C%20warm%20afternoon%20sunlight%2C%20charming%20small-town%20colonial%20architecture%2C%20no%20visible%20text%20or%20people%2C%20photorealistic%20landscape%20photography%20style&width=1800&height=700&seq=newcastle-de-hero-001&orientation=landscape",
  heroImageAlt: "Historic New Castle Delaware riverfront with colonial buildings along the Delaware River",
  geoPosition: "39.6621;-75.5663",
  geoPlacename: "New Castle, Delaware",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Philadelphia Passport Agency",
    address: "600 Arch St Suite 4200, Philadelphia, PA 19106",
    distance: "Approximately 30–40 minutes from New Castle, DE via I-95 N",
  },
  nearbyCitiesZips: [
    { name: "New Castle", zips: "19720, 19702" },
    { name: "Wilmington", zips: "19801, 19802, 19805, 19806, 19807, 19808, 19809, 19810" },
    { name: "Newark", zips: "19702, 19711, 19713, 19716" },
    { name: "Bear", zips: "19701" },
    { name: "Glasgow", zips: "19702" },
    { name: "Christiana", zips: "19702" },
    { name: "Pennsville (NJ)", zips: "08070" },
    { name: "Carneys Point (NJ)", zips: "08069" },
    { name: "Claymont", zips: "19703" },
    { name: "Stanton", zips: "19804" },
    { name: "Hockessin", zips: "19707" },
    { name: "Pike Creek", zips: "19808" },
    { name: "Elsmere", zips: "19805" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier for New Castle and Delaware River corridor departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "I-95 Corridor Specialist", desc: "Serving New Castle, Wilmington, and cross-border New Jersey travelers near the Delaware Memorial Bridge" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no trip to Philadelphia" },
  ],
  internalLinks: [
    { name: "Expedited passport in Wilmington, DE", route: "/cities/wilmington-de" },
    { name: "Emergency passport help in Dover, DE", route: "/cities/dover-de" },
    { name: "Urgent passport services in Newark, DE", route: "/cities/newark-de" },
    { name: "Delaware passport services overview", route: "/states/delaware" },
  ],
  fpcOfficeNote: "Fast Passport Center serves New Castle, Delaware remotely via secure registered courier. We do not have a walk-in office in New Castle — our registered couriers hand-deliver your documents to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for New Castle and Delaware River corridor travelers without the drive to Philadelphia.",
  fpcOfficeAddress: "1700 Market St, Suite 1005, Philadelphia, PA 19103",
  fpcOfficeDistance: "Serving New Castle, Wilmington, and the Delaware River corridor including cross-border New Jersey",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout the New Castle, Delaware area and the Delaware River corridor. From the historic colonial district to the modern neighborhoods near Wilmington Airport, from the I-95 corridor to the communities near the Delaware Memorial Bridge — we serve all of New Castle's diverse residential and historic communities.",
  nearbyCitiesIntro: "We serve New Castle and nearby communities including Wilmington, Newark, Bear, Glasgow, Christiana, Claymont, and cross-border New Jersey communities like Pennsville and Carneys Point. If you are within 30 minutes of New Castle, Delaware — on either side of the river — we have you covered.",
  howItWorksIntro: "Most New Castle travelers complete everything online and ship documents overnight. No need to drive 30–40 minutes to Philadelphia or take time off from work.",
  mistakesIntro: "New Castle, Delaware has a unique mix of historic district residents, cross-border New Jersey commuters, and airport-area workers. Here are the most common passport mistakes we see from New Castle travelers.",
  testimonialsIntro: "New Castle, Delaware travelers — from historic district residents to cross-border commuters — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in New Castle, DE? Here are the most common ones we hear from Delaware River corridor travelers.",
  statePageRoute: "/states/delaware",
  statePageName: "Delaware",
  dmvWebsite: "https://www.dmv.de.gov",
  dmvLabel: "Delaware DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 30–40 min to Philadelphia", "Full day commitment — challenging for commuters"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Perfect for commuter travel deadlines", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For New Castle travelers with immediate departure needs — as fast as 24 hours. Perfect for last-minute business trips and cross-border travel.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Business travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for New Castle adults and commuters with a passport issued within the last 15 years.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to New Castle address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for New Castle residents and families — including first international trips.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for New Castle and Delaware River corridor residents.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for New Castle families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for New Castle Delaware residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport New Castle DE", description: "Urgent passport processing for New Castle Delaware travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal New Castle DE", description: "Fast passport renewal for New Castle Delaware residents and commuters with a previously-issued passport." },
    { name: "First-Time Passport New Castle DE", description: "New passport application services for New Castle Delaware residents applying for their first passport." },
    { name: "Child Passport New Castle DE", description: "Minor passport services for New Castle Delaware families with children under 16." },
  ],
  testimonials: [
    {
      name: "Thomas B.",
      location: "New Castle, DE",
      rating: 5,
      text: "Work trip to Germany with four days notice. Fast Passport Center processed my renewal in 2 days. As someone who lives near the airport and travels frequently, this is the only service I will use.",
      service: "Passport Renewal",
    },
    {
      name: "Angela M.",
      location: "New Castle, DE",
      rating: 5,
      text: "First-time passport for our family cruise. They helped us find the right acceptance facility, guided us through the forms, and had all three passports delivered in 5 business days. Incredible from New Castle.",
      service: "Family Passport Bundle",
    },
    {
      name: "Richard P.",
      location: "Pennsville, NJ",
      rating: 5,
      text: "I live right across the Delaware Memorial Bridge in New Jersey. Fast Passport Center serves cross-border residents like me with the same great service. Renewed my passport in 3 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Linda K.",
      location: "Wilmington, DE",
      rating: 5,
      text: "Lost my passport before a business trip to Singapore. They handled the DS-64, guided me through the replacement process, and delivered in 48 hours. Outstanding for a busy professional near the I-95 corridor.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "newcastle-de-1",
      name: "New Castle Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "401 N Dupont Hwy",
      city: "New Castle",
      state: "DE",
      zip: "19720",
      phone: "(302) 322-6831",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Near downtown New Castle and historic district",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for New Castle and nearby Wilmington Airport area. Appointments required — book 1–2 weeks in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "newcastle-de-2",
      name: "Wilmington Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 N French St",
      city: "Wilmington",
      state: "DE",
      zip: "19801",
      phone: "(302) 658-5780",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 5 miles north of New Castle",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for northern New Castle residents and cross-border commuters. Call to confirm current passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "newcastle-de-3",
      name: "Christiana Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1700 Capitol Trail",
      city: "Newark",
      state: "DE",
      zip: "19702",
      phone: "(302) 738-7680",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Approximately 10 miles west of New Castle",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative for western New Castle County residents near the Christiana Mall area.",
      icon: "ri-mail-line",
    },
    {
      id: "newcastle-de-4",
      name: "New Castle County Clerk of Peace",
      type: "County Clerk Passport Acceptance",
      address: "800 N French St",
      city: "Wilmington",
      state: "DE",
      zip: "19801",
      phone: "(302) 395-7799",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "Approximately 5 miles north of New Castle in Wilmington",
      website: "https://www.nccde.org/",
      websiteLabel: "New Castle County",
      notes: "County-level facility with full passport acceptance. Extended hours compared to post office locations.",
      icon: "ri-government-line",
    },
    {
      id: "newcastle-de-5",
      name: "Pennsville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "401 N Broadway",
      city: "Pennsville",
      state: "NJ",
      zip: "08070",
      phone: "(856) 678-2600",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Across the Delaware Memorial Bridge in New Jersey",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Cross-border option for New Jersey residents who commute to New Castle. Call to confirm current passport acceptance hours.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited Philadelphia slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 30–40 min drive to Philadelphia", fpc: "No — ship from New Castle" },
    { feature: "Cross-Border Support", agency: "No flexibility for NJ commuters", fpc: "Yes — we serve NJ residents too" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "New Castle's busy commuters and airport workers often discover their passport needs just weeks before departure. Routine processing takes 6–13 weeks. Start expedited service at least 6 weeks before your trip.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Local photo services near the airport and industrial areas sometimes produce photos that meet general ID requirements but not passport standards. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 rejection reasons at New Castle and New Castle County acceptance facilities.",
    },
    {
      icon: "ri-map-pin-2-line",
      title: "Confusing New Castle, DE with New Castle, PA",
      description: "New Castle, Delaware is entirely separate from New Castle, Pennsylvania. Make sure all documents, addresses, and applications clearly specify New Castle, DE to avoid processing confusion.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Government fees change periodically. Always verify current fees before submitting — incorrect payment means starting over.",
    },
    {
      icon: "ri-time-line",
      title: "Underestimating Routine Processing Time",
      description: "New Castle residents often assume 4–6 weeks is enough. Routine processing takes 6–13 weeks. If your travel is within 10 weeks, use expedited service.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from your home near the historic district or airport area." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location near New Castle or Wilmington." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your New Castle address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in New Castle, DE?", a: "Fast Passport Center offers emergency passport processing in New Castle, Delaware in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Philadelphia Passport Agency (30–40 minutes from New Castle) — without you needing to make the drive." },
    { q: "Does New Castle, Delaware have its own passport agency?", a: "No — neither New Castle nor the state of Delaware has a regional passport agency. The closest facility is the Philadelphia Passport Agency, approximately 30–40 minutes from New Castle via I-95. Fast Passport Center's courier service means New Castle residents never need to make that drive." },
    { q: "Can I get expedited passport services if I live in New Jersey near the Delaware Memorial Bridge?", a: "Absolutely. Fast Passport Center serves cross-border communities including Pennsville, Carneys Point, and other New Jersey communities near the Delaware Memorial Bridge. Our service works the same way regardless of which side of the river you live on." },
    { q: "How much does an expedited passport cost in New Castle, DE?", a: "Government fees are fixed: $130 for a first-time passport book or $130 + $60 expedite for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your departure timeline." },
    { q: "What airports do New Castle, Delaware travelers use?", a: "New Castle is home to Wilmington Airport (ILG) at 141 N DuPont Hwy, which offers limited domestic service. Most international travelers use Philadelphia International Airport (PHL), approximately 30–40 minutes north via I-95. Baltimore-Washington International (BWI) is an alternative for some travelers. Always verify your passport is valid for at least 6 months past your return date." },
    { q: "Can I get a passport for my whole family in New Castle?", a: "Yes. Fast Passport Center handles multi-passport cases for families in New Castle, Wilmington, and the surrounding area. Whether you need child passports, renewals, or first-time applications, we coordinate the process to ensure everyone is ready to travel." },
    { q: "Where is the closest passport acceptance facility to New Castle?", a: "The New Castle Post Office at 401 N Dupont Hwy is the primary local acceptance facility. The Wilmington Post Office at 100 N French St is also nearby for northern residents. For county-level services, the New Castle County Clerk of Peace in Wilmington at 800 N French St offers extended hours. All require appointments." },
    { q: "How do I know if I need expedited or routine passport processing?", a: "If your travel is within 10 weeks, use expedited service. If your travel is within 2–3 weeks, use emergency expedited service. Routine processing takes 6–13 weeks and is only suitable for travel more than 3 months away. New Castle residents should plan ahead, especially for business travel and international flights." },
  ],
  photoLocations: [
    { name: "Walgreens — New Castle", address: "100 S Dupont Hwy, New Castle, DE 19720", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — New Castle", address: "300 E Basin Rd, New Castle, DE 19720", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — New Castle", address: "117 Wilton Blvd, New Castle, DE 19720", hours: "Mon–Sun: 6AM–11PM" },
    { name: "UPS Store — New Castle", address: "1105 N Dupont Hwy, New Castle, DE 19720", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
  ],
  dmvLocations: [
    { name: "DMV — New Castle", address: "2230 Hessler Blvd, New Castle, DE 19702", hours: "Mon–Fri: 8AM–4:30PM", note: "Serves New Castle and northern New Castle County. REAL ID processing available.", featured: true },
    { name: "DMV — Wilmington", address: "2500 W 4th St, Wilmington, DE 19805", hours: "Mon–Fri: 8AM–4:30PM", note: "Alternative for downtown Wilmington residents. REAL ID processing available.", featured: false },
  ],
  airports: [
    {
      code: "PHL",
      name: "Philadelphia International Airport",
      distance: "30–40 miles from New Castle, DE",
      address: "8000 Essington Ave, Philadelphia, PA 19153",
      note: "Primary international departure airport for New Castle travelers. 30–40 minute drive north on I-95. Major international hub with direct flights to Europe, the Caribbean, and beyond.",
      flightUrl: "https://www.phl.org/",
      flightLabel: "PHL Flight Info",
      featured: true,
    },
    {
      code: "ILG",
      name: "Wilmington Airport (New Castle)",
      distance: "5 miles from New Castle, DE",
      address: "141 N DuPont Hwy, New Castle, DE 19720",
      note: "Closest airport to New Castle with limited domestic service. Located in New Castle itself. No major international connections.",
      flightUrl: "https://www.flyilg.com/",
      flightLabel: "ILG Airport",
      featured: false,
    },
    {
      code: "BWI",
      name: "Baltimore-Washington International",
      distance: "60–70 miles from New Castle, DE",
      address: "7062 Elm Rd, Baltimore, MD 21240",
      note: "Alternative option for budget airlines and travelers with flexible routing.",
      flightUrl: "https://www.bwiairport.com/",
      flightLabel: "BWI Airport",
      featured: false,
    },
  ],
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified Delaware birth certificates from the Delaware Office of Vital Statistics. Available by mail, online, or in person.", url: "https://dhss.delaware.gov/dph/ss/vitalstats.html" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through the Delaware Office of Vital Statistics. Required for estate and legal matters.", url: "https://dhss.delaware.gov/dph/ss/vitalstats.html" },
    { icon: "ri-heart-pulse-line", label: "Marriage Certificate", desc: "Request certified marriage certificates from the New Castle County Clerk of the Peace. Marriage records maintained by the county where the ceremony took place.", url: "https://www.nccde.org/" },
    { icon: "ri-file-copy-2-line", label: "Divorce Decree", desc: "Obtain certified divorce decrees through the New Castle County Superior Court Prothonotary. Divorce records filed in the county where the decree was granted.", url: "https://courts.delaware.gov/" },
    { icon: "ri-global-line", label: "Order Online — VitalChek", desc: "Order Delaware birth, death, marriage, and divorce certificates online through VitalChek for Delaware River corridor residents.", url: "https://www.vitalchek.com/" },
  ],
};